import { useEffect } from 'react';
import { Button, Stack, TextField, Typography } from '@mui/material';
import React from 'react';
import useIsMounted from 'src/hooks/useIsMounted';
import TableGroups from './components/TableGroups';
import { useAdminPermission } from 'src/hooks/IAM/AdminPermission/useAdminPermission';
import { useUserIAM } from 'src/hooks/IAM/AdminUser/useUserIAM';
import { useAdminGroup } from 'src/hooks/IAM/AdminGroups/useAdminGroups';
import CreateGroup from './components/CreateGroup';
import BoxWithShadow from 'src/components/BoxWithShadow';

/**
 * The AdminGroup component handles the creation of a new admin group and displays all existing groups.
 *
 * @component
 * @example
 * // Usage:
 * <AdminGroup />
 *
 * This component includes:
 * - A form for creating a new group, which allows selecting permissions.
 * - A search bar for filtering groups.
 * - A table displaying all groups.
 * @remarks
 * - The effect will only run if the component is mounted, checked by `isMounted`.
 * - Dependencies: [isMounted]
 * @returns {JSX.Element} The rendered AdminGroup component.
 */
const AdminGroup = (): JSX.Element => {
  // Hooks to manage state and load data
  const { loadGroups, createGroupAdmin } = useAdminGroup();
  const isMounted = useIsMounted();
  const { group } = useAdminGroup();
  const { loadUsers } = useUserIAM();
  const { loadPermissions, selectedPermissions } = useAdminPermission();

  useEffect(() => {
    if (!isMounted) return;
    loadUsers({});
    loadPermissions({
      currentPage: 1,
      pageSize: 100,
    });
    loadGroups({});
  }, [isMounted]);

  return (
    <BoxWithShadow
      sx={{
        padding: 4,
      }}
    >
      {/* Form to create a new group */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          createGroupAdmin({
            ...group,
            permissions: selectedPermissions,
          });
        }}
      >
        <Typography variant="h4" mb={2}>
          Crear nuevo grupo
        </Typography>
        {/* Custom component to handle group creation fields */}
        <CreateGroup />
        <Button
          variant="contained"
          type="submit"
          sx={{
            width: '300px',
          }}
        >
          Crear
        </Button>
      </form>

      {/* Search bar and table header */}
      <Stack
        direction={'row'}
        justifyContent={'space-between'}
        sx={{ marginBlock: '20px' }}
      >
        <h2>Todos los grupos</h2>
        <TextField
          id="outlined-basic"
          label="Buscar grupo"
          variant="outlined"
          size="small"
        />
      </Stack>

      {/* Table to display all groups */}
      <TableGroups />
    </BoxWithShadow>
  );
};

export default AdminGroup;
